<script setup lang="ts">
import { usePage } from '@inertiajs/vue3';
import { CheckboxField } from '@vue-interface/checkbox-field';
import { intersection } from 'lodash-es';
import { reactive, watchEffect } from 'vue';
import type { Abilities } from '../types/generated';

const props = defineProps<{
    modelValue?: Abilities
}>();

const emit = defineEmits<{
    'update:modelValue': [value: Abilities]
}>();

export type UserAbilityChild = {
    label: string;
    name: string[];
    depends?: string[];
};

export type UserAbilityGroup = {
    name: string;
    label: string;
    children: UserAbilityChild[]
};

const page = usePage<{
    abilities: UserAbilityGroup[]
}>();

const model = reactive<Abilities>({
    send: props.modelValue?.send ?? {
        viewAny: false,
        view: false,
        create: false,
        update: false,
        delete: false,
        schedule: false,
        manageMgTemplate: false,
    },
    subscriber: props.modelValue?.subscriber ?? {
        viewAny: false,
        view: false,
        create: false,
        update: false,
        delete: false,
        upload: false,
    },
    template: props.modelValue?.template ?? {
        viewAny: false,
        view: false,
        create: false,
        update: false,
        delete: false,
    },
    list: props.modelValue?.list ?? {
        viewAny: false,
        view: false,
    },
});

function isGroupChecked(group: string): boolean {
    const groupAbilities = model[group as keyof Abilities];
    if (!groupAbilities) return false;
    return Object.values(groupAbilities).some(v => v === true);
}

function isPermissionChecked(group: string, permission: UserAbilityChild): boolean {
    const groupAbilities = model[group as keyof Abilities];
    if (!groupAbilities) return false;
    
    return permission.name.every(name => (groupAbilities as Record<string, boolean>)[name] === true);
}

function onClickGroup(event: Event, group: string) {
    const checked = (event.target as HTMLInputElement).checked;
    const groupAbilities = model[group as keyof Abilities];
    
    if (groupAbilities) {
        for (const key in groupAbilities) {
            (groupAbilities as Record<string, boolean>)[key] = checked;
        }
    }
}

function onClickPermission(event: Event, group: UserAbilityGroup, permission: UserAbilityChild) {
    const checked = (event.target as HTMLInputElement).checked;
    const groupAbilities = model[group.name as keyof Abilities];
    
    if (!groupAbilities) return;

    const abilities = groupAbilities as Record<string, boolean>;

    for (const name of permission.name) {
        abilities[name] = checked;
    }

    if (checked && permission.depends) {
        for (const name of permission.depends) {
            abilities[name] = true;
        }
    }
    else if (!checked) {
        for (const child of group.children) {
            if (!intersection(permission.name, child.depends).length) {
                continue;
            }

            for (const name of child.name) {
                abilities[name] = false;
            }
        }
    }
}

watchEffect(() => {
    emit('update:modelValue', model);
});
</script>

<template>
    <div class="flex flex-col gap-2">
        <div
            v-for="group in page.props.abilities"
            :key="group.name"
            class="flex flex-col gap-1">
            <label>
                <CheckboxField
                    :model-value="isGroupChecked(group.name)"
                    :label="group.label"
                    @click="onClickGroup($event, group.name)" />
            </label>
            <div
                v-for="(permission, i) in group.children"
                :key="`${group.name}-${i}`" 
                class="ml-4 ">
                <label>
                    <CheckboxField
                        type="checkbox"
                        :model-value="isPermissionChecked(group.name, permission)"
                        :label="permission.label"
                        @click="onClickPermission($event, group, permission)" /></label>
            </div>
        </div>
    </div>
</template>